import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TagInput from './TagInput';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white font-medium z-50 animate-slide-in ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    }`}>
      <div className="flex items-center gap-2">
        <span>{type === 'success' ? '✅' : '❌'}</span>
        <span>{message}</span>
        <button 
          onClick={onClose}
          className="ml-2 text-white hover:text-gray-200"
        >
          ×
        </button>
      </div>
    </div>
  );
};

interface InstallationStep {
  title: string;
  content: string;
  commands: string[];
  order: number;
  additionalTitle?: string;
  additionalContent?: string;
  additionalCode?: string;
  additionalTitle2?: string;
  additionalContent2?: string;
  additionalCode2?: string;
  additionalTitle3?: string;
  additionalContent3?: string;
  additionalCode3?: string;
}

interface Configuration {
  title: string;
  content: string;
  configCode: string;
  order: number;
}

interface Command {
  title: string;
  description: string;
  command: string;
  category: string;
}

interface Project {
  _id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  imageUrl: string;
  projectUrl: string;
  category: string;
  network: string;
  operationType: string;
  status?: string;
  requirements?: {
    hardware: {
      cpu: string;
      ram: string;
      storage: string;
    };
    recommended: {
      cpu: string;
      ram: string;
      storage: string;
    };
  };
  installationSteps: InstallationStep[];
  configurations: Configuration[];
  commands: Command[];
  links?: {
    github?: string;
    twitter?: string;
    discord?: string;
    website?: string;
  };
  tags: string[];
  featured: boolean;
  isPublished: boolean;
  projectType?: string;
  categoriesContent?: Record<string, InstallationStep[]>;
  categoryOrder?: string[];
  createdAt: string;
}

interface FormData {
  title: string;
  description: string;
  content: string;
  imageUrl: string;
  projectUrl: string;
  github: string;
  twitter: string;
  discord: string;
  website: string;
  projectType: string;
  category: string;
  network: string;
  requirements: {
    hardware: {
      cpu: string;
      ram: string;
      storage: string;
    };
    recommended: {
      cpu: string;
      ram: string;
      storage: string;
    };
  };
  tags: string[];
  featured: boolean;
  isPublished: boolean;
}

interface ProjectManagerProps {
  token: string;
  externalShowForm?: boolean;
  onToggleForm?: () => void;
  defaultCategory?: string;
}

const ProjectManager: React.FC<ProjectManagerProps> = ({ token, externalShowForm, onToggleForm, defaultCategory = 'Installation' }) => {
  console.log('ProjectManager started with defaultCategory:', defaultCategory);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Use external form control if provided
  const actualShowForm = externalShowForm !== undefined ? externalShowForm : showForm;
  const toggleForm = onToggleForm || (() => setShowForm(!showForm));
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Filter projects based on search term
  const filteredProjects = Array.isArray(projects) ? projects.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.slug.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProjects = filteredProjects.slice(startIndex, endIndex);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);



  // Clear editing project when component mounts (when switching sections)
  React.useEffect(() => {
    setEditingProject(null);
    setShowForm(false);
  }, []);

  // Toast functions
  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Function to clear all form states
  const clearFormStates = React.useCallback(() => {
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      content: '',
      imageUrl: '',
      projectUrl: '',
      github: '',
      twitter: '',
      discord: '',
      website: '',
      projectType: 'default',
      category: defaultCategory, // Set default category based on props
      network: 'mainnet',
      requirements: {
        hardware: { cpu: '', ram: '', storage: '' },
        recommended: { cpu: '', ram: '', storage: '' }
      },
      tags: [],
      featured: false,
      isPublished: true
    });
    setCategoriesByType({
      cosmos: ['Automatic Installation', 'Manual', 'Updates', 'Public API', 'Sync', 'Cheat Sheet'],
      default: ['Installation', 'Updates', 'Cheat Sheet', 'Delete'],
      custom: ['Installation', 'Updates', 'Services', 'Cheat Sheet', 'Delete'],
    });
    setCategoriesContent({});
    setEditingSteps({});
    setActiveFields({});
    setExpandedCategories({});
  }, [defaultCategory]);

  // Functions for managing additional fields
  const getStepKey = (category: string, index: number) => `${category}-${index}`;
  
  const addAdditionalField = (category: string, index: number, fieldType: 'Title' | 'Content' | 'Code') => {
    const stepKey = getStepKey(category, index);
    const currentFields = activeFields[stepKey] || {};
    
    // Find next available field number
    let fieldNumber = 1;
    const baseFieldName = `additional${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)}`;
    
    while (currentFields[`${baseFieldName}${fieldNumber === 1 ? '' : fieldNumber}`]) {
      fieldNumber++;
      if (fieldNumber > 3) break; // Limit to 3 additional fields of each type
    }
    
    const newFieldName = `${baseFieldName}${fieldNumber === 1 ? '' : fieldNumber}`;
    
    setActiveFields(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        [newFieldName]: true
      }
    }));
  };

  const removeAdditionalField = (category: string, index: number, fieldName: string) => {
    const stepKey = getStepKey(category, index);
    
    setActiveFields(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        [fieldName]: false
      }
    }));
    
    // Clear the field value
    updateStep(category, index, fieldName as keyof InstallationStep, '');
  };

  const getActiveFieldsForStep = (category: string, index: number) => {
    const stepKey = getStepKey(category, index);
    return activeFields[stepKey] || {};
  };

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    content: '',
    imageUrl: '',
    projectUrl: '',
    github: '',
    twitter: '',
    discord: '',
    website: '',
    projectType: 'default',
    category: defaultCategory,
    network: 'mainnet',
    requirements: {
      hardware: { cpu: '', ram: '', storage: '' },
      recommended: { cpu: '', ram: '', storage: '' }
    },
    tags: [],
    featured: false,
    isPublished: false,
  });

  const [categoriesByType, setCategoriesByType] = useState<Record<string, string[]>>({
    cosmos: ['Automatic Installation', 'Manual', 'Updates', 'Public API', 'Sync', 'Cheat Sheet'],
    default: ['Installation', 'Updates', 'Cheat Sheet', 'Delete'],
    custom: ['Installation', 'Updates', 'Services', 'Cheat Sheet', 'Delete'],
  });

  const getAvailableCategories = (projectType: string) => {
    return categoriesByType[projectType] || [];
  };

  const addCategory = (type: string) => {
    const newCategory = prompt('Enter new category name:');
    if (!newCategory) return;
    setCategoriesByType(prev => {
      const arr = prev[type] || [];
      if (arr.includes(newCategory)) return prev;
      return { ...prev, [type]: [...arr, newCategory] };
    });
  };

  const removeCategory = (type: string, categoryToRemove: string) => {
    setCategoriesByType(prev => {
      const arr = (prev[type] || []).filter(cat => cat !== categoryToRemove);
      
      // If we're removing the currently selected category, select the first available one
      if (formData.category === categoryToRemove && arr.length > 0) {
        setFormData(prevForm => ({ ...prevForm, category: arr[0] }));
      }
      
      return { ...prev, [type]: arr };
    });
    
    // Remove content for this category
    setCategoriesContent(prev => {
      const copy = { ...prev };
      delete copy[categoryToRemove];
      return copy;
    });
  };

  // content per category: array of InstallationStep per category name
  const [categoriesContent, setCategoriesContent] = useState<Record<string, InstallationStep[]>>({});
  const [editingSteps, setEditingSteps] = useState<Record<string, boolean>>({});
  const [activeFields, setActiveFields] = useState<Record<string, { [key: string]: boolean }>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const toggleEditStep = (category: string, index: number) => {
    const key = getStepKey(category, index);
    setEditingSteps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveStep = (category: string, index: number) => {
    const step = categoriesContent[category]?.[index];
    const key = getStepKey(category, index);
    setEditingSteps(prev => ({ ...prev, [key]: false }));
    
    const stepName = step?.title?.trim() || `Step ${index + 1}`;
    showToast(`"${stepName}" saved successfully!`, 'success');
  };

  const toggleCategoryExpansion = (categoryName: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const moveCategoryUp = (type: string, categoryIndex: number) => {
    if (categoryIndex <= 0) return;
    setCategoriesByType(prev => {
      const arr = [...(prev[type] || [])];
      [arr[categoryIndex - 1], arr[categoryIndex]] = [arr[categoryIndex], arr[categoryIndex - 1]];
      return { ...prev, [type]: arr };
    });
  };

  const moveCategoryDown = (type: string, categoryIndex: number) => {
    const categories = categoriesByType[type] || [];
    if (categoryIndex >= categories.length - 1) return;
    setCategoriesByType(prev => {
      const arr = [...(prev[type] || [])];
      [arr[categoryIndex], arr[categoryIndex + 1]] = [arr[categoryIndex + 1], arr[categoryIndex]];
      return { ...prev, [type]: arr };
    });
  };

  const moveStepUp = (category: string, stepIndex: number) => {
    if (stepIndex <= 0) return;
    setCategoriesContent(prev => {
      const arr = [...(prev[category] || [])];
      // Swap positions
      [arr[stepIndex - 1], arr[stepIndex]] = [arr[stepIndex], arr[stepIndex - 1]];
      // Update order values to match new positions
      arr.forEach((step, index) => {
        step.order = index + 1;
      });
      return { ...prev, [category]: arr };
    });
  };

  const moveStepDown = (category: string, stepIndex: number) => {
    const steps = categoriesContent[category] || [];
    if (stepIndex >= steps.length - 1) return;
    setCategoriesContent(prev => {
      const arr = [...(prev[category] || [])];
      // Swap positions
      [arr[stepIndex], arr[stepIndex + 1]] = [arr[stepIndex + 1], arr[stepIndex]];
      // Update order values to match new positions
      arr.forEach((step, index) => {
        step.order = index + 1;
      });
      return { ...prev, [category]: arr };
    });
  };

  // Функція для генерації URL з назви проекту
  const generateProjectUrl = (title: string): string => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Видаляємо всі символи окрім літер, цифр, пробілів і дефісів
      .replace(/\s+/g, '-') // Замінюємо пробіли на дефіси
      .replace(/-+/g, '-') // Замінюємо множинні дефіси на один
      .replace(/^-|-$/g, ''); // Видаляємо дефіси на початку і в кінці
  };

  // Обробник зміни назви проекту з автоматичним заповненням Project URL
  const handleTitleChange = (title: string) => {
    const projectUrl = generateProjectUrl(title);
    setFormData(prev => ({
      ...prev,
      title: title,
      projectUrl: projectUrl
    }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await axios.post('/api/upload/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      if (response.data.url) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.url }));
        console.log('Logo uploaded to:', response.data.url);
        showToast('Logo uploaded successfully!', 'success');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      showToast('Failed to upload logo. Please try again.', 'error');
    }
  };

  const addStep = (category: string) => {
    const newStep: InstallationStep = { 
      title: '', 
      content: '', 
      commands: [], 
      order: (categoriesContent[category]?.length || 0) + 1,
      additionalTitle: '',
      additionalContent: '',
      additionalCode: ''
    };
    const newIndex = (categoriesContent[category]?.length || 0);
    setCategoriesContent(prev => ({ ...prev, [category]: [...(prev[category] || []), newStep] }));
    
    // Automatically put new step in edit mode
    const newStepKey = getStepKey(category, newIndex);
    setEditingSteps(prev => ({ ...prev, [newStepKey]: true }));
  };

  const updateStep = (category: string, index: number, field: keyof InstallationStep, value: any) => {
    setCategoriesContent(prev => {
      const arr = prev[category] ? [...prev[category]] : [];
      const step = { ...arr[index], [field]: value };
      arr[index] = step;
      return { ...prev, [category]: arr };
    });
  };

  const removeStep = (category: string, index: number) => {
    setCategoriesContent(prev => {
      const arr = prev[category] ? [...prev[category]] : [];
      arr.splice(index, 1);
      // Update order values after removal to maintain correct sequence
      arr.forEach((step, i) => {
        step.order = i + 1;
      });
      return { ...prev, [category]: arr };
    });
  };



  useEffect(() => {
    fetchProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure categoriesContent has entries for current project's available categories and update category when projectType changes
  useEffect(() => {
    // Skip this effect if we're editing a project - handleEdit already set everything up correctly
    if (editingProject) {
      return;
    }
    
    const avail = categoriesByType[formData.projectType] || [];
    
    setCategoriesContent(prev => {
      const copy = { ...prev };
      
      // Add missing categories from available list
      avail.forEach(cat => {
        if (!copy[cat]) {
          copy[cat] = [];
        }
      });
      
      // Don't remove categories for custom projects - they manage their own
      if (formData.projectType !== 'custom') {
        // Remove categories that are not in available list for non-custom types
        Object.keys(copy).forEach(key => {
          if (!avail.includes(key)) {
            delete copy[key];
          }
        });
      }
      
      return copy;
    });
  }, [formData.projectType, categoriesByType, editingProject]);

  // Separate effect to handle category updates when projectType changes
  useEffect(() => {
    // Skip this effect if we're editing a project - handleEdit already set the correct category
    if (editingProject) {
      return;
    }
    
    const availableCategories = categoriesByType[formData.projectType] || [];
    if (availableCategories.length > 0 && (!formData.category || !availableCategories.includes(formData.category))) {
      setFormData(prev => ({
        ...prev,
        category: availableCategories[0]
      }));
    }
  }, [formData.projectType, categoriesByType, formData.category, editingProject]);

  const fetchProjects = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await axios.get(`${apiUrl}/api/projects/admin`, {
        withCredentials: true,
      });
      console.log('Projects response:', response.data);
      // Handle the backend response structure: {success: true, data: {projects: [...], pagination: {...}}}
      const allProjects = response.data.success && response.data.data ? response.data.data.projects : [];
      
      // Фільтруємо проекти - показуємо тільки НЕ Stake категорію для Services
      const servicesProjects = allProjects.filter((project: any) => project.category !== 'Stake');
      setProjects(servicesProjects);
      
      console.log('Total projects:', allProjects.length, 'Services projects:', servicesProjects.length);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== FORM SUBMIT DEBUG ===');
    console.log('1. defaultCategory received in ProjectManager:', defaultCategory);
    console.log('2. formData.category:', formData.category);
    console.log('3. About to override category with defaultCategory');
    
    const projectData = {
      ...formData,
      // Keep description from formData (now includes our Description field)
      content: '', // Field removed from UI but kept for backend compatibility
      tags: formData.tags,
      // Force correct category based on defaultCategory
      category: defaultCategory,
      links: {
        github: formData.github,
        twitter: formData.twitter,
        discord: formData.discord,
        website: formData.website,
      },
      categoriesContent,
      // Save the category order to maintain sequence on public frontend
      categoryOrder: categoriesByType[formData.projectType] || [],
    };

    console.log('4. Final projectData category:', projectData.category);
    console.log('5. Sending to backend with category:', projectData.category);
    console.log('=== END DEBUG ===');

    try {
      if (editingProject) {
        await axios.put(
          `/api/projects/${editingProject._id}`,
          projectData,
          { withCredentials: true }
        );
      } else {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        await axios.post(`${apiUrl}/api/projects`, projectData, {
          withCredentials: true,
        });
      }

      resetForm();
      fetchProjects();
      showToast(editingProject ? 'Project updated successfully!' : 'Project created successfully!', 'success');
    } catch (error: any) {
      console.error('Error saving project:', error);
      let errorMessage = 'Failed to save project';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showToast(errorMessage, 'error');
    }
  };

  const handleEdit = (project: Project) => {
    console.log('=== EDITING PROJECT ===');
    console.log('Project from API:', JSON.stringify(project, null, 2));
    console.log('Project.projectType:', project.projectType);
    console.log('Project.categoryOrder:', project.categoryOrder);
    console.log('Project.categoriesContent keys:', project.categoriesContent ? Object.keys(project.categoriesContent) : 'NO CONTENT');
    
    setEditingProject(project);
    
    // Initialize activeFields for existing additional fields
    const initialActiveFields: Record<string, { additionalTitle: boolean; additionalContent: boolean; additionalCode: boolean }> = {};
    if (project.categoriesContent) {
      Object.entries(project.categoriesContent).forEach(([category, steps]) => {
        steps.forEach((step, index) => {
          const stepKey = getStepKey(category, index);
          initialActiveFields[stepKey] = {
            additionalTitle: !!step.additionalTitle,
            additionalContent: !!step.additionalContent,
            additionalCode: !!step.additionalCode,
          };
        });
      });
    }
    setActiveFields(initialActiveFields);
    setFormData({
      title: project.title,
      description: project.description,
      content: project.content || '',
      imageUrl: project.imageUrl,
      projectUrl: project.projectUrl,
      github: project.links?.github || '',
      twitter: project.links?.twitter || '',
      discord: project.links?.discord || '',
      website: project.links?.website || '',
      projectType: project.projectType || 'cosmos',
      category: project.category || defaultCategory,
      network: project.network || 'mainnet',
      requirements: project.requirements || {
        hardware: { cpu: '', ram: '', storage: '' },
        recommended: { cpu: '', ram: '', storage: '' }
      },
      tags: project.tags || [],
      featured: project.featured,
      isPublished: project.isPublished || false,
    });
    
    // If project already has categoriesContent saved, load it
    if (project.categoriesContent) {
      setCategoriesContent(project.categoriesContent || {});
      const currentProjectType = project.projectType || 'cosmos';
      
      // Use categoryOrder if available to restore exact order and custom categories
      const categoryOrder = project.categoryOrder && project.categoryOrder.length > 0 
        ? project.categoryOrder 
        : Object.keys(project.categoriesContent || {});
      
      console.log('Using categoryOrder:', categoryOrder);
      console.log('For projectType:', currentProjectType);
      
      if (categoryOrder.length > 0) {
        // Directly set the categories for this type without trying to merge
        const newCategoriesByType = {
          cosmos: ['Automatic Installation', 'Manual', 'Updates', 'Public API', 'Sync', 'Cheat Sheet'],
          default: ['Installation', 'Updates', 'Cheat Sheet', 'Delete'],
          custom: ['Installation', 'Updates', 'Services', 'Cheat Sheet', 'Delete'],
        };
        
        // Restore the exact order from categoryOrder
        newCategoriesByType[currentProjectType as keyof typeof newCategoriesByType] = categoryOrder;
        
        console.log('NEW categoriesByType:', newCategoriesByType);
        
        setCategoriesByType(newCategoriesByType);
      }
    } else {
      // Project doesn't have categoriesContent, initialize with default for its type
      const projectType = project.projectType || 'cosmos';
      
      if (projectType === 'custom') {
        // For custom projects without categoriesContent, initialize empty custom categories
        setCategoriesByType(prev => ({ ...prev, custom: [] }));
        setCategoriesContent({});
      } else {
        // For standard project types, use their default categories
        const defaultCategories = categoriesByType[projectType as keyof typeof categoriesByType] || categoriesByType.cosmos;
        const defaultCategoriesContent: Record<string, InstallationStep[]> = {};
        
        defaultCategories.forEach(cat => {
          defaultCategoriesContent[cat] = project.installationSteps?.filter((step: any) => step.category === cat) || [];
        });
        
        setCategoriesContent(defaultCategoriesContent);
      }
    }
    if (!onToggleForm) {
      setShowForm(true);
    } else {
      onToggleForm();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) return;

    try {
      await axios.delete(`/api/projects/${id}`, {
        withCredentials: true,
      });
      fetchProjects();
      showToast('Service deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting project:', error);
      showToast('Error deleting service', 'error');
    }
  };

  const resetForm = () => {
    console.log('resetForm called with defaultCategory:', defaultCategory);
    setFormData({
      title: '',
      description: '',
      content: '',
      imageUrl: '',
      projectUrl: '',
      github: '',
      twitter: '',
      discord: '',
      website: '',
      projectType: 'default',
      category: defaultCategory,
      network: 'mainnet',
      requirements: {
        hardware: { cpu: '', ram: '', storage: '' },
        recommended: { cpu: '', ram: '', storage: '' }
      },
      tags: [],
      featured: false,
      isPublished: false,
    });
    const defaultCategories = [defaultCategory, 'Installation', 'Updates', 'Cheat Sheet', 'Delete'].filter((cat, index, arr) => arr.indexOf(cat) === index);
    console.log('Setting categories with defaultCategory:', defaultCategory, 'resulting array:', defaultCategories);
    setCategoriesByType({
      cosmos: ['Automatic Installation', 'Manual', 'Updates', 'Public API', 'Sync', 'Cheat Sheet'],
      default: ['Installation', 'Updates', 'Cheat Sheet', 'Delete'],
      custom: ['Installation', 'Updates', 'Services', 'Cheat Sheet', 'Delete'],
    });
    setCategoriesContent({});
    setEditingSteps({});
    setActiveFields({});
    setExpandedCategories({});
    setEditingProject(null);
    if (!onToggleForm) {
      setShowForm(false);
    } else {
      onToggleForm();
    }
  };

  if (loading) {
    return <div className="text-white">Loading projects...</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Services</h1>
            <p className="text-gray-400">Manage blockchain infrastructure services and guides</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Update Project button - show only when editing */}
            {editingProject && actualShowForm && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                className="px-6 py-3 bg-gradient-to-r from-dusk-copper-deep to-dusk-copper hover:from-dusk-copper hover:to-dusk-amber text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/30 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Update
              </button>
            )}
            
            <button
              onClick={() => {
                clearFormStates();
                toggleForm();
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {actualShowForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Services
                </>
              )}
            </button>
          </div>
        </div>

        {actualShowForm && (
          <form 
            key={editingProject ? `edit-${editingProject._id}` : 'create-new'} 
            onSubmit={handleSubmit} 
            className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm"
          >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-300">
                  Logo
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    className="px-3 py-2 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white text-xs font-medium rounded-lg transition-all"
                  >
                    Upload
                  </button>
                  {formData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="px-3 py-2 bg-red-900/50 hover:bg-red-900/70 border border-red-700/50 text-red-300 text-xs font-medium rounded-lg transition-all"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <input
                type="text"
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="Upload a logo or enter URL manually"
                readOnly={formData.imageUrl.startsWith('/uploads/')}
              />
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              {formData.imageUrl && (
                <div className="mt-2 p-2 bg-[rgba(243,235,226,0.04)] rounded">
                  <p className="text-xs text-gray-400 mb-2">Logo Preview:</p>
                  <img 
                    src={getFullImageUrl(formData.imageUrl)} 
                    alt="Logo preview" 
                    className="w-16 h-16 object-contain bg-white rounded border"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Description field - full width */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white h-24 resize-none"
              placeholder="Enter project description..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Project URL
              </label>
              <input
                type="text"
                value={formData.projectUrl}
                onChange={(e) => setFormData({ ...formData, projectUrl: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="/Starknet, /Cosmos, etc."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                GitHub URL
              </label>
              <input
                type="url"
                value={formData.github}
                onChange={(e) => setFormData({ ...formData, github: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="https://github.com/username/repo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Twitter URL
              </label>
              <input
                type="url"
                value={formData.twitter}
                onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="https://twitter.com/username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Discord URL
              </label>
              <input
                type="url"
                value={formData.discord}
                onChange={(e) => setFormData({ ...formData, discord: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="https://discord.gg/invite-link"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Website URL
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <TagInput
                value={formData.tags}
                onChange={(tags) => setFormData({ ...formData, tags })}
                placeholder="Start typing to search or add new tags..."
                maxTags={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Network
              </label>
              <select
                value={formData.network}
                onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                required
              >
                <option value="mainnet">Mainnet</option>
                <option value="testnet">Testnet</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Project Type
              </label>
              <select
                value={formData.projectType}
                onChange={(e) => {
                  const newType = e.target.value;
                  
                  // Define standard categories for each type
                  const standardCategories = {
                    custom: ['Installation', 'Updates', 'Services', 'Cheat Sheet', 'Delete'],
                    default: ['Installation', 'Updates', 'Cheat Sheet', 'Delete'],
                    cosmos: ['Automatic Installation', 'Manual', 'Updates', 'Public API', 'Sync', 'Cheat Sheet']
                  };
                  
                  // When switching types, preserve current type's custom categories and restore new type's
                  setCategoriesByType(prev => {
                    const updated = { ...prev };
                    
                    // Get current custom/user-added categories for new type
                    const currentTypeCategories = updated[formData.projectType] || [];
                    const standardForCurrentType = standardCategories[formData.projectType as keyof typeof standardCategories] || [];
                    
                    // Save any custom categories from current type (those not in standard list)
                    const customForCurrentType = currentTypeCategories.filter(
                      cat => !standardForCurrentType.includes(cat)
                    );
                    
                    // Update the current type with its preserved categories
                    if (customForCurrentType.length > 0) {
                      updated[formData.projectType] = [...standardForCurrentType, ...customForCurrentType];
                    }
                    
                    // Now restore or initialize the new type
                    const standardForNewType = standardCategories[newType as keyof typeof standardCategories] || [];
                    const existingCustomCategories = updated[newType] || [];
                    
                    // Remove standard categories from existing, keep only custom ones
                    const customForNewType = existingCustomCategories.filter(
                      cat => !Object.values(standardCategories).flat().includes(cat)
                    );
                    
                    // Restore new type with its standard + custom categories
                    updated[newType] = [...standardForNewType, ...customForNewType];
                    
                    return updated;
                  });
                  
                  setCategoriesContent({}); // Clear old content when switching types
                  setFormData(prev => ({ 
                    ...prev, 
                    projectType: newType,
                    category: newType === 'custom' ? 'Installation' : 
                              newType === 'default' ? 'Installation' :
                              'Automatic Installation'
                  }));
                }}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                required
              >
                <option value="cosmos">Cosmos</option>
                <option value="default">Default</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">
                  Category
                </label>
                {(formData.projectType === 'custom' || formData.projectType === 'cosmos' || formData.projectType === 'default') && (
                  <button
                    type="button"
                    onClick={() => addCategory(formData.projectType)}
                    className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded"
                  >
                    +Add
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {getAvailableCategories(formData.projectType).map((cat) => (
                  <div key={cat} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, category: cat })}
                      className={`px-3 py-1 rounded text-sm ${
                        formData.category === cat
                          ? 'bg-dusk-copper text-white'
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {cat}
                    </button>
                    {((formData.projectType === 'custom') || (formData.projectType === 'cosmos') || (formData.projectType === 'default')) && (
                      <button
                        type="button"
                        onClick={() => removeCategory(formData.projectType, cat)}
                        className="ml-1 px-1 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg font-medium text-gray-300 mb-2">Hardware Requirements</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.requirements.hardware.cpu}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, hardware: { ...formData.requirements.hardware, cpu: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="CPU (e.g., 6 cores)"
                />
                <input
                  type="text"
                  value={formData.requirements.hardware.ram}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, hardware: { ...formData.requirements.hardware, ram: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="RAM (e.g., 8GB)"
                />
                <input
                  type="text"
                  value={formData.requirements.hardware.storage}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, hardware: { ...formData.requirements.hardware, storage: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="Storage (e.g., 500GB SSD)"
                />
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-300 mb-2">Recommended Requirements</h4>
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.requirements.recommended.cpu}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, recommended: { ...formData.requirements.recommended, cpu: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="CPU (e.g., 8 cores)"
                />
                <input
                  type="text"
                  value={formData.requirements.recommended.ram}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, recommended: { ...formData.requirements.recommended, ram: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="RAM (e.g., 16GB)"
                />
                <input
                  type="text"
                  value={formData.requirements.recommended.storage}
                  onChange={(e) => setFormData({ ...formData, requirements: { ...formData.requirements, recommended: { ...formData.requirements.recommended, storage: e.target.value } } })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white text-sm"
                  placeholder="Storage (e.g., 1TB NVME)"
                />
              </div>
            </div>
          </div>

          {/* Categories content per selected Project Type */}
          <div className="mt-6 p-4 bg-[var(--dusk-bg)] rounded-lg border border-gray-700">
            <h3 className="text-xl font-semibold text-white mb-4">Category Content</h3>
            {getAvailableCategories(formData.projectType).map((cat, catIndex) => (
              <div key={cat} className="mb-4 p-4 bg-[rgba(243,235,226,0.04)] rounded border border-gray-700">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategoryExpansion(cat)}
                      className="flex items-center gap-2 text-left"
                    >
                      <h4 className="text-lg font-semibold text-white">{cat}</h4>
                      <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
                        {(categoriesContent[cat] || []).length} {(categoriesContent[cat] || []).length === 1 ? 'step' : 'steps'}
                      </span>
                      <svg 
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                          expandedCategories[cat] ? 'rotate-0' : '-rotate-90'
                        }`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveCategoryUp(formData.projectType, catIndex)}
                        disabled={catIndex === 0}
                        className={`px-2 py-1 text-white text-sm rounded ${
                          catIndex === 0 
                            ? 'bg-gray-500 cursor-not-allowed' 
                            : 'bg-dusk-copper hover:bg-dusk-copper-deep'
                        }`}
                        title="Move category up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCategoryDown(formData.projectType, catIndex)}
                        disabled={catIndex === getAvailableCategories(formData.projectType).length - 1}
                        className={`px-2 py-1 text-white text-sm rounded ${
                          catIndex === getAvailableCategories(formData.projectType).length - 1 
                            ? 'bg-gray-500 cursor-not-allowed' 
                            : 'bg-dusk-copper hover:bg-dusk-copper-deep'
                        }`}
                        title="Move category down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => addStep(cat)}
                      className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded"
                    >
                      + Add Step
                    </button>
                    <button
                      type="button"
                      onClick={() => removeCategory(formData.projectType, cat)}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded"
                    >
                      Remove Category
                    </button>
                  </div>
                </div>

                <div className={`transition-all duration-300 overflow-hidden ${
                  !expandedCategories[cat] ? 'max-h-0 opacity-0' : 'max-h-none opacity-100'
                }`}>
                {expandedCategories[cat] && (
                  <>
                {(categoriesContent[cat] || []).map((step, idx) => {
                  const stepKey = getStepKey(cat, idx);
                  const isEditing = editingSteps[stepKey] || false;
                  
                  return (
                  <div key={idx} className="mb-3 p-3 bg-[rgba(243,235,226,0.06)] rounded">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-400">
                        {step.title?.trim() ? `Step ${idx + 1}: ${step.title}` : `Step ${idx + 1}`}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveStepUp(cat, idx)}
                          disabled={idx === 0}
                          className={`px-2 py-1 text-white text-xs rounded ${
                            idx === 0 
                              ? 'bg-gray-500 cursor-not-allowed' 
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                          title="Move step up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStepDown(cat, idx)}
                          disabled={idx === (categoriesContent[cat] || []).length - 1}
                          className={`px-2 py-1 text-white text-xs rounded ${
                            idx === (categoriesContent[cat] || []).length - 1 
                              ? 'bg-gray-500 cursor-not-allowed' 
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                          title="Move step down"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={step.title}
                      onChange={(e) => updateStep(cat, idx, 'title', e.target.value)}
                      className={`w-full px-2 py-1 border rounded text-white mb-2 ${
                        isEditing 
                          ? 'bg-[rgba(243,235,226,0.04)] border-dusk-copper' 
                          : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                      }`}
                      placeholder="Step title (optional, e.g., Install Bridge Node)"
                      readOnly={!isEditing}
                    />
                    <textarea
                      value={step.content}
                      onChange={(e) => updateStep(cat, idx, 'content', e.target.value)}
                      className={`w-full px-2 py-2 border rounded text-white mb-2 ${
                        isEditing 
                          ? 'bg-[rgba(243,235,226,0.04)] border-dusk-copper' 
                          : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                      }`}
                      rows={3}
                      placeholder="Enter step description (supports line breaks):\nRegister on Alchemy\nPress + CREATE APP\nChoose Ethereum Mainnet"
                      readOnly={!isEditing}
                    />
                    <label className="text-sm text-gray-300 mb-1 block">Commands (optional)</label>
                    <textarea
                      value={(step.commands || []).join('\n')}
                      onChange={(e) => updateStep(cat, idx, 'commands', e.target.value.split('\n'))}
                      className={`w-full px-2 py-2 border rounded text-white font-mono text-sm ${
                        isEditing 
                          ? 'bg-[rgba(243,235,226,0.04)] border-dusk-copper' 
                          : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                      }`}
                      rows={4}
                      placeholder={`Commands (optional):\ncd $HOME\nrm -rf repo\ngit clone ...`}
                      readOnly={!isEditing}
                    />
                    
                    {/* Dynamic Additional Fields */}
                    {isEditing && (
                      <div className="mt-4 space-y-2">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => addAdditionalField(cat, idx, 'Title')}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm flex items-center gap-1"
                          >
                            + Add Title
                          </button>
                          <button
                            type="button"
                            onClick={() => addAdditionalField(cat, idx, 'Content')}
                            className="px-3 py-1 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded text-sm flex items-center gap-1"
                          >
                            + Add Description
                          </button>
                          <button
                            type="button"
                            onClick={() => addAdditionalField(cat, idx, 'Code')}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm flex items-center gap-1"
                          >
                            + Add Code
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Dynamic Additional Fields */}
                    {Object.entries(getActiveFieldsForStep(cat, idx)).map(([fieldName, isActive]) => {
                      if (!isActive) return null;
                      
                      const fieldValue = (step as any)[fieldName] || '';
                      const isTitle = fieldName.includes('Title');
                      const isContent = fieldName.includes('Content');
                      const isCode = fieldName.includes('Code');
                      
                      return (
                        <div key={fieldName} className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-sm text-gray-300">
                              {isTitle ? 'Additional Title' : isContent ? 'Additional Description' : 'Additional Code'} (optional)
                            </label>
                            {isEditing && (
                              <button
                                type="button"
                                onClick={() => removeAdditionalField(cat, idx, fieldName)}
                                className="text-red-400 hover:text-red-300 text-sm"
                              >
                                ✕ Remove
                              </button>
                            )}
                          </div>
                          {isTitle ? (
                            <input
                              type="text"
                              value={fieldValue}
                              onChange={(e) => updateStep(cat, idx, fieldName as keyof InstallationStep, e.target.value)}
                              className={`w-full px-2 py-1 border rounded text-white mb-2 ${
                                isEditing 
                                  ? 'bg-[rgba(243,235,226,0.04)] border-dusk-amber' 
                                  : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                              }`}
                              placeholder="Additional title (e.g., Important Note)"
                              readOnly={!isEditing}
                            />
                          ) : (
                            <textarea
                              value={fieldValue}
                              onChange={(e) => updateStep(cat, idx, fieldName as keyof InstallationStep, e.target.value)}
                              className={`w-full px-2 py-2 border rounded text-white ${isCode ? 'font-mono text-sm' : ''} ${
                                isEditing 
                                  ? `bg-[rgba(243,235,226,0.04)] border-${isContent ? 'blue' : 'green'}-500` 
                                  : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                              }`}
                              rows={isCode ? 4 : 3}
                              placeholder={isContent ? "Additional description or notes..." : `Additional code or configuration:\n[Unit]\nDescription=Example Service\nAfter=network.target`}
                              readOnly={!isEditing}
                            />
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Show existing fields that might not be in activeFields */}
                    {['additionalTitle', 'additionalContent', 'additionalCode', 'additionalTitle2', 'additionalContent2', 'additionalCode2', 'additionalTitle3', 'additionalContent3', 'additionalCode3'].map(fieldName => {
                      const fieldValue = (step as any)[fieldName];
                      if (!fieldValue || getActiveFieldsForStep(cat, idx)[fieldName]) return null;
                      
                      const isTitle = fieldName.includes('Title');
                      const isContent = fieldName.includes('Content');
                      const isCode = fieldName.includes('Code');
                      
                      return (
                        <div key={fieldName} className="mt-3">
                          <label className="text-sm text-gray-300 mb-1 block">
                            {isTitle ? 'Additional Title' : isContent ? 'Additional Description' : 'Additional Code'} (optional)
                          </label>
                          {isTitle ? (
                            <input
                              type="text"
                              value={fieldValue}
                              onChange={(e) => updateStep(cat, idx, fieldName as keyof InstallationStep, e.target.value)}
                              className={`w-full px-2 py-1 border rounded text-white mb-2 ${
                                isEditing 
                                  ? 'bg-[rgba(243,235,226,0.04)] border-dusk-amber' 
                                  : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                              }`}
                              placeholder="Additional title (e.g., Important Note)"
                              readOnly={!isEditing}
                            />
                          ) : (
                            <textarea
                              value={fieldValue}
                              onChange={(e) => updateStep(cat, idx, fieldName as keyof InstallationStep, e.target.value)}
                              className={`w-full px-2 py-2 border rounded text-white ${isCode ? 'font-mono text-sm' : ''} ${
                                isEditing 
                                  ? `bg-[rgba(243,235,226,0.04)] border-${isContent ? 'blue' : 'green'}-500` 
                                  : 'bg-gray-600 border-[var(--dusk-line-strong)]'
                              }`}
                              rows={isCode ? 4 : 3}
                              placeholder={isContent ? "Additional description or notes..." : `Additional code or configuration:\n[Unit]\nDescription=Example Service\nAfter=network.target`}
                              readOnly={!isEditing}
                            />
                          )}
                        </div>
                      );
                    })}
                    <div className="flex gap-2 mt-2">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => saveStep(cat, idx)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                          >
                            Save Step
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleEditStep(cat, idx)}
                            className="px-3 py-1 bg-gray-600 hover:bg-[rgba(243,235,226,0.06)] text-white rounded text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleEditStep(cat, idx)}
                          className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm"
                        >
                          Edit Step
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeStep(cat, idx)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                      >
                        Delete Step
                      </button>
                    </div>
                  </div>
                  );
                })}
                {/* Add Step button at the bottom of each category */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => addStep(cat)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded inline-flex items-center gap-2"
                  >
                    <span>+</span> Add Another Step
                  </button>
                </div>
                  </>
                )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-300">Visibility:</span>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="adminOnly"
                  name="visibility"
                  checked={!formData.isPublished}
                  onChange={() => setFormData({ ...formData, isPublished: false })}
                  className="mr-2"
                />
                <label htmlFor="adminOnly" className="text-sm font-medium text-gray-300 mr-4">
                  Admin Only
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="public"
                  name="visibility"
                  checked={formData.isPublished}
                  onChange={() => setFormData({ ...formData, isPublished: true })}
                  className="mr-2"
                />
                <label htmlFor="public" className="text-sm font-medium text-gray-300">
                  Public
                </label>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-300">
                Featured Project
              </label>
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
            >
              {editingProject ? 'Update Project' : 'Create Project'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Services Table */}
      <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Network
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Visibility
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {currentProjects.map((project) => (
                <tr key={project._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all duration-300 border-[var(--dusk-line)]">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {project.imageUrl ? (
                          <img
                            className="h-10 w-10 rounded-lg object-cover shadow-md"
                            src={getFullImageUrl(project.imageUrl)}
                            alt={project.title}
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {project.title}
                        </div>
                        <div className="text-sm text-gray-400 max-w-xs truncate">
                          {project.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`admin-badge ${
                        (project.network || 'mainnet') === 'mainnet'
                          ? 'admin-badge-network-mainnet'
                          : (project.network || 'mainnet') === 'testnet'
                            ? 'admin-badge-network-testnet'
                            : (project.network || 'mainnet') === 'devnet'
                              ? 'admin-badge-network-devnet'
                              : 'admin-badge-network-other'
                      }`}
                    >
                      {project.network || 'mainnet'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`admin-badge ${
                        project.status === 'inactive'
                          ? 'admin-badge-status-inactive'
                          : project.status === 'maintenance'
                            ? 'admin-badge-status-maintenance'
                            : 'admin-badge-status-active'
                      }`}
                    >
                      {project.status || 'active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col items-start gap-1.5">
                      <span
                        className={`admin-badge ${
                          project.isPublished
                            ? 'admin-badge-visibility-public'
                            : 'admin-badge-visibility-private'
                        }`}
                      >
                        {project.isPublished ? 'Public' : 'Admin Only'}
                      </span>
                      {project.featured && (
                        <span className="admin-badge admin-badge-featured">Featured</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <a
                        href={`/services/${project.network || 'mainnet'}/${project.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-dusk-amber hover:text-dusk-amber hover:bg-[rgba(208,0,104,0.15)] rounded-lg transition-all"
                        title="View Page"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                      <button
                        onClick={() => handleEdit(project)}
                        className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(project._id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredProjects.length)} of {filteredProjects.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm bg-[rgba(243,235,226,0.06)] text-gray-300 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                // Show first page, last page, current page and adjacent pages
                const shouldShow = page === 1 || 
                                   page === totalPages || 
                                   Math.abs(page - currentPage) <= 1;
                
                if (!shouldShow && page === 2 && currentPage > 4) {
                  return <span key={page} className="px-3 py-2 text-gray-400">...</span>;
                }
                if (!shouldShow && page === totalPages - 1 && currentPage < totalPages - 3) {
                  return <span key={page} className="px-3 py-2 text-gray-400">...</span>;
                }
                if (!shouldShow) return null;

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      currentPage === page
                        ? 'bg-dusk-copper text-white'
                        : 'bg-[rgba(243,235,226,0.06)] text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm bg-[rgba(243,235,226,0.06)] text-gray-300 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      </div>



      {/* Toast notifications */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProjectManager;
